import { Placement } from "./Placement";
import {
  BODY_SKINS,
  DIRECTION_LEFT,
  DIRECTION_RIGHT,
  INVENTORY,
  PLACEMENT_TYPE_BULLET,
  PLACEMENT_TYPE_PARTICLE,
  directionUpdateMap,
  Z_INDEX_LAYER_SIZE,
  CELL_SIZE,
} from "../helpers/consts";
import sounds, { SFX } from "../classes/Sounds";
import { Collision } from "../classes/Collision";

const FREEZE_FRAMES_AFTER_SINGLE_MOVE = 2; //Gap between allowing to move again;
const FREEZE_FRAMES_AFTER_TELEPORT = 20;

export class BodyPlacement extends Placement {
  constructor(properties, level) {
    super(properties, level);

    // Counts down frames until we are unlocked after stepping on a tile that automatically moves the body
    // (Freezes you on the auto movement tile for a bit before moving)
    this.autoMovementLockedFrames = 0;

    // Counts down frames until we are allowed to manually move again
    this.postMovementLockedFrames = 0;

    // Number of frames to count before trying to move again (for Enemies, really)
    this.waitFramesBeforeAIMove = 0;
    this.movingPixelDirection = properties.direction ?? DIRECTION_RIGHT;

    // This is just visual... chooses the Tile that is drawn in the React component
    this.facingDirection = properties.direction;

    // Mostly just for Hero, but swimming enemies could have this, too
    this.skin = BODY_SKINS.NORMAL;
    this.walkFrame = 1;
  }

  _handleAIMove() {
    //Implemented by subclass
  }

  tick() {
    //Optional post tick for enemy hero detection
    // This seems to fix a bug where hero could sometimes pass through the enemy
    if (this.enemyTickCheckForHero) {
      const [heroX, heroY] = this.level.heroRef.displayXY();
      const [myX, myY] = this.displayXY();
      const xDist = Math.abs(myX - heroX);
      const yDist = Math.abs(myY - heroY);
      if (xDist < 2 && yDist < 2) {
        if (this.handleEnemyCollideWithHero) {
          this.handleEnemyCollideWithHero();
        }
      }
    }

    //Work on pixel movement if we are moving
    this.tickMovingPixelProgress();

    // If locked before next move, count down those frames before doing anything
    if (this.autoMovementLockedFrames > 0) {
      this.autoMovementLockedFrames -= 1;
      if (this.autoMovementLockedFrames === 0) {
        this._doAutomaticMoveFromCurrentSpace();
      }
      return;
    }

    // If we just moved somewhere, lock us here for a second (for grid-feeling movement)
    if (this.postMovementLockedFrames > 0) {
      this.postMovementLockedFrames -= 1;
      return;
    }

    // If not locked, count down until next automatic decision is made (For enemies, really)
    this.framesUntilAiMove -= 1;
    if (this.framesUntilAiMove === 0) {
      this.framesUntilAiMove = this.waitFramesBeforeAIMove;
      this._handleAIMove();
    }
  }

  tickMovingPixelProgress() {
    if (this.movingPixelsRemaining === 0) {
      return;
    }
    this.movingPixelsRemaining -= this.travelPixelsPerFrame;
    if (this.movingPixelsRemaining <= 0) {
      this.movingPixelsRemaining = 0;
      this.onMoveFinished();
    }
  }

  _doAutomaticMoveFromCurrentSpace() {
    // Check for current space that will tell you what to do next
    const collision = new Collision(this, this.level);

    // Handle Ice that moves you in movement direction
    const slipperyIceTile = collision.withIceTileCausesSlipping();
    if (slipperyIceTile) {
      sounds.playSfx(SFX.ICE);
      const nextDirection = slipperyIceTile.getNextDirectionForBody(this);
      if (this._nextSpaceIsFree(nextDirection)) {
        this._move(nextDirection);
        return;
      }
    }

    // Handle Conveyor space that moves you to the direction shown.
    const onConveyorSpace = collision.withConveyorTile();
    if (onConveyorSpace) {
      sounds.playSfx(SFX.CONVEYOR);
      const nextDirection = onConveyorSpace.direction;
      if (this._nextSpaceIsFree(nextDirection)) {
        this._move(nextDirection);
        return;
      }
    }

    // Handle Teleport Space
    const onTeleportSpace = collision.withTeleportTile();
    const teleportToPosition =
      onTeleportSpace?.teleportsToPositionOnUnlock(this);
    if (teleportToPosition) {
      sounds.playSfx(SFX.TELEPORT);
      this.postMovementLockedFrames = FREEZE_FRAMES_AFTER_TELEPORT;
      this.level.updateLivePlacement(this, {
        x: teleportToPosition.x,
        y: teleportToPosition.y,
      });
    }
  }

  _nextSpaceIsFree(directionString) {
    const collision = new Collision(this, this.level);

    // If currently on ice corner, check certain paths
    if (!collision.withTileAllowsMovementInDirection(directionString)) {
      return false;
    }

    // Check next space
    const { x, y } = directionUpdateMap[directionString];
    const nextX = this.x + x;
    const nextY = this.y + y;
    return this.level.isSpaceFree(nextX, nextY, this);
  }

  _move(directionString) {
    // Don't allow setting while we're still working
    if (this.movingPixelsRemaining > 0) {
      return;
    }
    this.movingPixelsRemaining = CELL_SIZE;
    this.movingPixelDirection = directionString;
    if (
      directionString === DIRECTION_LEFT ||
      directionString === DIRECTION_RIGHT
    ) {
      this.facingDirection = directionString;
    }

    //Flip animation frame
    this.walkFrame = this.walkFrame === 1 ? 2 : 1;
  }

  onMoveFinished() {
    this.updatePosition(this.movingPixelDirection);
    this._updateLockedFramesFromCurrentTile(); //Should this read "updateLockedFrames"? //Like the others. The name implies it should return the value
    this._handleCollisions();

    if (this.autoMovementLockedFrames === 0) {
      // Lock frames to not be able to move again
      this.postMovementLockedFrames = FREEZE_FRAMES_AFTER_SINGLE_MOVE;
    }
  }

  updatePosition(directionString) {
    const { x, y } = directionUpdateMap[directionString];
    this.x = this.x + x;
    this.y = this.y + y;
  }

  //Conveyors and Ice will LOCK you
  //Ice boots might not lock you if you have the boots
  _updateLockedFramesFromCurrentTile() {
    const collision = new Collision(this, this.level);
    const lockingFramesTile = collision.withTileGivesLockedFrames();
    if (lockingFramesTile) {
      this.autoMovementLockedFrames =
        lockingFramesTile.givesPreAutoMoveFrames(this);
    }
  }

  _handleCollisions() {
    const collision = new Collision(this, this.level);

    //Update skin on this space
    this.level.updateLivePlacement(this, {
      skin: collision.getSkinForCurrentTile(),
    });

    //Check for anything that adds to inventory
    const collideThatAddsToInventory = collision.withTileAddsToInventory();
    if (collideThatAddsToInventory && this.level.isPlayingMode) {
      this.level.collectTile(collideThatAddsToInventory, this);
      this.level.spawnLivePlacement({
        type: PLACEMENT_TYPE_PARTICLE,
        x: collideThatAddsToInventory.x,
        y: collideThatAddsToInventory.y,
      });
    }

    //Purple switches
    if (collision.withTileSwitchesDoors()) {
      sounds.playSfx(SFX.DOOR_SWITCH);
      this.level.switchWalls();
    }

    //Thief collide
    if (collision.withTileResetsInventory()) {
      sounds.playSfx(SFX.THUMP);
      this.level.resetAllInventory();
    }

    //Drops Bullets in dropbox
    if (collision.withTileDropsBullet()) {
      this.level.spawnLivePlacement({
        type: PLACEMENT_TYPE_BULLET,
        x: this.x,
        y: this.y,
      });
      this.level.removeFromInventory(INVENTORY.BULLET_PICKUP);
    }

    // Complete level on collide
    if (collision.withCompletesLevelTile()) {
      this.level.completeLevel();
    }

    // "I take damage from this thing"
    const takesDamageFrom = collision.withTileDamagesBody();
    if (takesDamageFrom && this.takeDamage) {
      this.takeDamage(takesDamageFrom);
    }

    // "Collides with something to which I give damage"
    const givesDamageTo = collision.withBodyDamagesOther();
    if (givesDamageTo && givesDamageTo.takeDamage) {
      givesDamageTo.takeDamage(this);
    }

    //I am on Ice
    // const ice = collision.withIceTile();
    // if (ice) {
    //   console.log(ice);
    //   this._move("UP");
    // }
  }

  getZIndex() {
    return this.y * Z_INDEX_LAYER_SIZE;
  }
}
